import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

//components
import Loader from 'wrapper/loader';

//libs
import { AnimatePresence } from 'framer-motion';
import { motion } from 'framer-motion';

//animations
import { scaleMotionProps } from 'constants/animationProps';

//constants
import { getUserDetails } from 'auth/auth';

//pages
import Page0 from './sub-pages/Page-0';
import Page1 from './sub-pages/Page-1';
import Page2 from './sub-pages/Page-2';
import Page3 from './sub-pages/Page-3';
import Page4 from './sub-pages/Page-4';
import Page5 from './sub-pages/Page-5';

//API queries
import {
  useLazyGetOnboardingDataQuery,
  useCreateOnboardingMutation,
  useUpdateOnboardingMutation,
} from 'redux_store/services/client/onboarding';

import layoutStyles from '../../styles/features/onboarding-layout.module.css';

const Onboarding = ({ setIsOnboarding }) => {
  const { t } = useTranslation(['onboarding']);
  //sates of all pages
  const [page, setPage] = useState(0);
  const [direction, setDirection] = useState(null);
  const [pageState, setPageState] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedOfficer, setSelectedOfficer] = useState('');
  const [selectedOfficerObj, setSelectedOfficerObj] = useState(null);
  const [address, setAddress] = useState('');
  const [loanPurpose, setLoanPurpose] = useState('');
  const [employment, setEmployment] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [progressSeed, setProgressSeed] = useState(0);

  const buyerSlides = [
    {
      image:
        'https://images.unsplash.com/photo-1633158829585-23ba8f7c8caf?q=80&w=2000&auto=format&fit=crop',
      title: t('onboarding:slides.findPlace.title'),
      highlight: t('onboarding:slides.findPlace.highlight'),
    },
    {
      image:
        'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=2000&auto=format&fit=crop',
      title: t('onboarding:slides.buildFuture.title'),
      highlight: t('onboarding:slides.buildFuture.highlight'),
    },
    {
      image:
        'https://images.unsplash.com/photo-1563986768609-322da13575f3?q=80&w=2000&auto=format&fit=crop',
      title: t('onboarding:slides.unlockHome.title'),
      highlight: t('onboarding:slides.unlockHome.highlight'),
    },
  ];

  //get user details from token
  const { firstName } = getUserDetails();
  const user = firstName;

  //API queries
  const [
    fetchOnboarding,
    { data, error, isFetching },
  ] = useLazyGetOnboardingDataQuery();
  const [createOnboarding, { isLoading: createLoading }] =
    useCreateOnboardingMutation();
  const [updateOnboarding, { isLoading: updateLoading }] =
    useUpdateOnboardingMutation();

  useEffect(() => {
    fetchOnboarding();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentSlide((prev) => (prev + 1) % buyerSlides.length);
      setProgressSeed((seed) => seed + 1);
    }, 6000);

    return () => clearTimeout(timer);
  }, [currentSlide]);

  const renderSlideTitle = (slide) => {
    const parts = slide.title.split(slide.highlight);
    if (parts.length === 1) return slide.title;
    return (
      <>
        {parts[0]}
        <span className={layoutStyles.slideHighlight}>{slide.highlight}</span>
        {parts.slice(1).join(slide.highlight)}
      </>
    );
  };
  
  const saveOnboardingData = async () => {
    // Prevent multiple simultaneous saves
    if (isSaving) {
      return;
    }

    // Initialize an empty object
    let onboarding = {};

    // Conditionally add fields to the object
    if (selectedOption !== undefined && selectedOption !== null) {
      onboarding.isWorkingWithAnyLoanOfficer = selectedOption;
    }

    if (address && address.trim() !== '') {
      onboarding.address = address;
    }
    if (!address && page === 3) {
      onboarding.address = null;
      onboarding.lenderId = null;
    }

    if (selectedOfficerObj?.userId) {
      onboarding.lenderId = selectedOfficerObj.userId;
    }
    if (!selectedOfficerObj && page === 2) {
      onboarding.lenderId = null;
    }

    if (loanPurpose) {
      onboarding.loanPurposeId =
        loanPurpose === 'refinance' ? 2 : loanPurpose === 'purchase' ? 1 : 0;
    }

    if (!selectedOfficerObj && (page === 2 || page === 3)) {
      onboarding.loanPurposeId = null;
    }
    if (employment) {
      onboarding.employmentTypeId =
        employment === 'employed'
          ? 2
          : employment === 'self'
            ? 3
            : employment === 'retired'
              ? 1
              : 0;
    } else if (page === 5 && data?.data?.employmentTypeId) {
      onboarding.employmentTypeId = data.data.employmentTypeId;
    }

    // Always include these fields
    // onboarding.statusId = 1;
    onboarding.pageNumber = page > 0 ? page : 1;

    if (error?.status === 404) {
      // Always update pageState to match page for first-time users
      setPageState(page);

      // Only create onboarding profile when user has made a selection
      if (
        onboarding?.isWorkingWithAnyLoanOfficer === true ||
        onboarding?.isWorkingWithAnyLoanOfficer === false
      ) {
        setIsSaving(true);
        try {
          await createOnboarding({ data: onboarding });
          setTimeout(() => {
            fetchOnboarding();
          }, 300);
        } finally {
          setIsSaving(false);
        }
      }
    } else if (data?.status === 200) {
      setIsSaving(true);
      try {
        await updateOnboarding({ data: onboarding });
        setPageState(page);
        setTimeout(() => {
          fetchOnboarding();
        }, 300);
      } finally {
        setIsSaving(false);
      }
    }
  };

  //populate data from localStorage if it exists
  useEffect(() => {
    if (data?.data) {
      // Check if onboarding is complete - all required fields must be present
      const isOnboardingComplete =
        data?.data?.pageNumber === 5 &&
        data?.data?.employmentTypeId &&
        data?.data?.loanPurposeId &&
        data?.data?.isWorkingWithAnyLoanOfficer !== null &&
        data?.data?.isWorkingWithAnyLoanOfficer !== undefined;

      if (isOnboardingComplete) {
        setIsOnboarding(false);
        return; // Exit early to prevent unnecessary state updates
      }

      setPage(data?.data?.pageNumber);
      setSelectedOption(
        data?.data?.isWorkingWithAnyLoanOfficer === '0' ? false : true
      );
      setSelectedOfficerObj(
        data?.data?.lenderId ? { userId: data?.data?.lenderId } : null
      );
      setAddress(data?.data?.address ? data?.data?.address : '');
      setLoanPurpose(
        data?.data?.loanPurposeId === 2
          ? 'refinance'
          : data?.data?.loanPurposeId === 1
            ? 'purchase'
            : ''
      );
      setEmployment(
        data?.data.employmentTypeId === 2
          ? 'employed'
          : data?.data.employmentTypeId === 3
            ? 'self'
            : data?.data.employmentTypeId === 1
              ? 'retired'
              : ''
      );
    }
  }, [data]);

  // save onboarding data on page change
  useEffect(() => {
    saveOnboardingData();
  }, [page]);

  // if onboarding data is false show page 0 for 2 seconds and start onboarding
  useEffect(() => {
    if (error?.status === 404) {
      const timer = setTimeout(() => {
        setPage(1);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [error]);

  return (
    <div className={layoutStyles.root}>
      <div className={layoutStyles.carouselPane}>
        <div className={layoutStyles.carouselInner}>
          {buyerSlides.map((slide, index) => (
            <div
              key={index}
              className={`${layoutStyles.slide} ${
                index === currentSlide ? layoutStyles.slideActive : layoutStyles.slideInactive
              }`}
            >
              <div className={layoutStyles.slideOverlay} />
              <div className={layoutStyles.carouselBlur} />
              <img
                src={slide.image}
                alt={slide.title}
                className={layoutStyles.slideImage}
              />
              <div className={layoutStyles.slideText}>
                <h2 className={layoutStyles.slideTitle}>{renderSlideTitle(slide)}</h2>
              </div>
            </div>
          ))}
          <div className={layoutStyles.dots}>
            {buyerSlides.map((_, index) => (
              <button
                key={index}
                type="button"
                aria-label={t('onboarding:goToSlide', { index: index + 1 })}
                onClick={() => {
                  setCurrentSlide(index);
                  setProgressSeed((seed) => seed + 1);
                }}
                className={`${layoutStyles.dotButton} ${
                  index === currentSlide ? layoutStyles.dotActive : layoutStyles.dotInactive
                }`}
              >
                {index === currentSlide && (
                  <span
                    className={layoutStyles.dotProgress}
                    key={`progress-${index}-${progressSeed}`}
                  />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className={layoutStyles.rightPane}>
        <div className={layoutStyles.contentPane}>
          <AnimatePresence mode="wait">
            <motion.div {...scaleMotionProps} className={layoutStyles.card}>
              {isFetching && <Loader  variant="gray" FullScreen={false} />}
              <div className={layoutStyles.pageContent}>
                {pageState === 0 && error?.status === 404 && (
                  <Page0 user={user} />
                )}
                {pageState === 1 && !isFetching && (
                  <Page1
                    user={user}
                    isLoading={createLoading || updateLoading}
                    selectedOption={selectedOption}
                    setPage={setPage}
                    setSelectedOption={setSelectedOption}
                    setAddress={setAddress}
                    setSelectedOfficer={setSelectedOfficer}
                    setSelectedOfficerObj={setSelectedOfficerObj}
                    setLoanPurpose={setLoanPurpose}
                    setEmployment={setEmployment}
                    data={data?.data}
                    setDirection={setDirection}
                    direction={direction}
                  />
                )}
                {pageState === 2 && !isFetching && (
                  <Page2
                    isLoading={updateLoading}
                    user={user}
                    setAddress={setAddress}
                    address={address}
                    setPage={setPage}
                    setDirection={setDirection}
                    direction={direction}
                  />
                )}
                {pageState === 3 && !isFetching && (
                  <Page3
                    isLoading={createLoading || updateLoading}
                    user={user}
                    address={address}
                    selectedOfficer={selectedOfficer}
                    setSelectedOfficer={setSelectedOfficer}
                    setSelectedOfficerObj={setSelectedOfficerObj}
                    selectedOfficerObj={selectedOfficerObj}
                    selectedOption={selectedOption}
                    setPage={setPage}
                    setDirection={setDirection}
                    direction={direction}
                  />
                )}
                {pageState === 4 && !isFetching && (
                  <Page4
                    isLoading={createLoading || updateLoading}
                    user={user}
                    setPage={setPage}
                    loanPurpose={loanPurpose}
                    setLoanPurpose={setLoanPurpose}
                    setDirection={setDirection}
                    direction={direction}
                  />
                )}
                {pageState === 5 && !isFetching && (
                  <Page5
                    isLoading={createLoading || updateLoading}
                    user={user}
                    setPage={setPage}
                    employment={employment}
                    setEmployment={setEmployment}
                    setIsOnboarding={setIsOnboarding}
                    dataForSubmission={{
                      loanPurpose: loanPurpose,
                      lenderId: selectedOfficerObj?.userId,
                      address: address ? address : 'N/A',
                    }}
                    saveOnboardingData={saveOnboardingData}
                    setDirection={setDirection}
                    direction={direction}
                  />
                )}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
